import { containsBannedDash } from '../text/dashes';
import type { TechniqueEntry } from './schema';

// Mechanical enforcement of the ADR-109 authoring rules the Zod schema cannot
// express structurally. Pure functions, no I/O — consumed by the co-located
// test suite (the CI gate) and importable by any future authoring tooling.
//
// Four violation classes:
//   1. A quoted span inside a BARE STRING in `method`/`serves`/`never` — every
//      payload field is prompt-strength, so a quoted line outside the two typed
//      exception forms is treated as a parroted model utterance. The typed
//      forms (memberVoiced span; prohibited-phrase constraint) are exempt
//      structurally. `doneWhen` is deliberately not scanned (member-side
//      listen-for markers may be quoted there).
//   2. A `firstMove` ending in a tag question or carrying a repeatable
//      confirmation tic — live-proven to recycle turn after turn. An
//      interrogative firstMove is fine; the bar is verdict-seeking tags
//      appended to statements.
//   3. A decision annotation inside any entry field — date-stamped or
//      attributed parentheticals, pending-decision markers. Decisions live in
//      the catalogue doc and tickets, never in signed content.
//   4. An em or en dash (ADR-159) in any Sona-facing field. Every such field
//      is rendered into the practice Sona reads, and models imitate the
//      punctuation of their context. `source` is provenance that never reaches
//      Sona, so it alone is exempt.

// Straight or curly double-quoted span, plus TYPOGRAPHIC single-quote PAIRS
// (smart-quote source docs emit ‘…’ for utterances; a paired curly single
// quote is unambiguously a quotation, unlike a straight apostrophe, which
// stays prose).
const QUOTED_SPAN = /"[^"]+"|“[^”]+”|‘[^’]+’/;

// The four banned confirmation tags, anywhere in a firstMove.
const BANNED_CONFIRMATION_TAGS = [
  'is that right',
  'is that close',
  'how close am i',
  'does that land',
] as const;

// Verdict-seeking endings: "does that sound/resonate…?", or a short trailing
// tag ("…, right?" / "…close?" / "…yeah?"). Deliberately narrow so genuine
// interrogative first moves (readiness question, screening question) pass.
const TAG_QUESTION_ENDINGS = [
  /\bdoes that (sound|resonate)[^?]*\?\s*$/i,
  /\b(right|close|yeah)\?\s*$/i,
  /,\s*(ok|okay|no|you know|isn'?t it|does(?:n'?t)? it)\?\s*$/i,
];

// A parenthetical is a decision annotation when it carries a decision verb,
// a pending-decision marker, a date stamp, or a version-supersession note.
// Deliberately form-based, never name-based: ADR-109 mandates provenance
// citations in `source` (e.g. 'James et al., 2019'), so matching on author
// names would false-positive on compliant entries; attributed decision notes
// still trip the decided/deliberate/pending forms.
const PARENTHETICAL = /\(([^)]*)\)/g;
const MONTH = '(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)';
const DECISION_SIGNALS: readonly RegExp[] = [
  /\b(deliberate|decided|agreed)\b/i,
  /\bclinical call needed\b/i,
  /\bpending\b/i,
  /\bneeds? a call\b/i,
  /\bto confirm\b/i,
  /\bsupersedes\b/i,
  new RegExp('\\b\\d{1,2}(?:st|nd|rd|th)? ' + MONTH + '\\b', 'i'),
  new RegExp('\\b' + MONTH + '[a-z]* \\d{1,2}\\b', 'i'),
  // Numeric date stamps (2026-08-19, 19/08, 19/8/26) — the month-name forms
  // above miss them.
  /\b\d{4}-\d{2}-\d{2}\b/,
  /\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b/,
];

function quotedSpanViolations(field: string, text: string): string[] {
  if (!QUOTED_SPAN.test(text)) return [];
  return [
    field +
      ': bare string carries a quoted span (model utterance outside the typed exception forms): ' +
      text,
  ];
}

function firstMoveViolations(firstMove: string): string[] {
  const violations: string[] = [];
  const lower = firstMove.toLowerCase();
  for (const tag of BANNED_CONFIRMATION_TAGS) {
    if (lower.includes(tag)) {
      violations.push('firstMove: carries the banned confirmation tag "' + tag + '"');
    }
  }
  // The ending patterns anchor on `?$`; strip trailing closers (quotes,
  // brackets) first so `…right?"` or `…yeah?)` cannot defeat the anchor.
  const stripped = firstMove.replace(/[\s"”'’)\]]+$/, '');
  if (violations.length === 0 && TAG_QUESTION_ENDINGS.some((pattern) => pattern.test(stripped))) {
    violations.push('firstMove: ends in a tag question / verdict-seeking tic: ' + firstMove);
  }
  return violations;
}

function decisionAnnotationViolations(field: string, text: string): string[] {
  const violations: string[] = [];
  const clauses: string[] = [];
  // exec loop instead of matchAll: consumers typecheck this source at pre-es2015 targets
  const pattern = new RegExp(PARENTHETICAL.source, PARENTHETICAL.flags);
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    clauses.push(match[1] ?? '');
  }
  // Dash-delimited clauses are the other annotation container smart-quote
  // source docs produce ("… — decided 19 Aug"): every em/en-dash segment
  // after the first is scanned with the same narrow signals.
  const dashSegments = text.split(/\s[—–]\s?|[—–]\s/).slice(1);
  clauses.push(...dashSegments);
  for (const inner of clauses) {
    if (DECISION_SIGNALS.some((signal) => signal.test(inner))) {
      violations.push(
        field + ': decision annotation inside an entry field: (' + inner.trim() + ')',
      );
    }
  }
  return violations;
}

// Fields whose text never reaches Sona, so citation page ranges may keep their dashes.
const DASH_EXEMPT_FIELDS: ReadonlySet<string> = new Set(['source']);

function bannedDashViolations(field: string, text: string): string[] {
  if (DASH_EXEMPT_FIELDS.has(field) || !containsBannedDash(text)) return [];
  return [`${field}: contains an em or en dash; rewrite it per the ADR-159 replacement table`];
}

/** Every string an entry carries, flattened with a field label — for the field-agnostic decision-annotation scan. */
function allStringFields(entry: TechniqueEntry): ReadonlyArray<readonly [string, string]> {
  const fields: Array<readonly [string, string]> = [
    ['name', entry.name],
    ['pillar', entry.pillar],
    ['serves', entry.serves],
    ['doneWhen', entry.doneWhen],
    ['source', entry.source],
  ];
  entry.method.forEach((item, i) => {
    if (typeof item === 'string') fields.push([`method[${i}]`, item]);
    else fields.push([`method[${i}].memberVoiced`, item.memberVoiced]);
  });
  entry.never.forEach((item, i) => {
    if (typeof item === 'string') fields.push([`never[${i}]`, item]);
    else {
      fields.push([`never[${i}].constraint`, item.constraint]);
      fields.push([`never[${i}].phrase`, item.phrase]);
    }
  });
  entry.requiresFirst.forEach((item, i) =>
    fields.push([`requiresFirst[${i}].clause`, item.clause]),
  );
  entry.exemplars.forEach((item, i) => fields.push([`exemplars[${i}]`, item]));
  if (entry.firstMove !== null) fields.push(['firstMove', entry.firstMove]);
  return fields;
}

// Fields that never reach Sona: the citation and the open taxonomy label.
const NOT_SONA_FACING: ReadonlySet<string> = new Set(['source', 'pillar']);

/** Every string of an entry that is sent to Sona, flattened with a field label. */
export function sonaFacingStringFields(
  entry: TechniqueEntry,
): ReadonlyArray<readonly [string, string]> {
  // A label is the field name, optionally followed by an index or sub-field.
  const fieldName = (label: string) => label.split(/[.[]/, 1)[0] ?? label;
  return allStringFields(entry).filter(([label]) => !NOT_SONA_FACING.has(fieldName(label)));
}

/** Scan one entry against the four authoring-rule violation classes; returns human-readable violation descriptions (empty = clean). */
export function scanTechniqueEntry(entry: TechniqueEntry): string[] {
  const violations: string[] = [];

  violations.push(...quotedSpanViolations('serves', entry.serves));
  entry.method.forEach((item, i) => {
    if (typeof item === 'string') violations.push(...quotedSpanViolations(`method[${i}]`, item));
  });
  entry.never.forEach((item, i) => {
    if (typeof item === 'string') violations.push(...quotedSpanViolations(`never[${i}]`, item));
  });

  if (entry.firstMove !== null) violations.push(...firstMoveViolations(entry.firstMove));

  for (const [field, text] of allStringFields(entry)) {
    violations.push(...decisionAnnotationViolations(field, text));
    violations.push(...bannedDashViolations(field, text));
  }

  return violations;
}

/** Scan a set of entries; returns a map of entry key → violations for entries with at least one violation (empty map = clean catalogue). */
export function scanTechniqueCatalogue(entries: readonly TechniqueEntry[]): Map<string, string[]> {
  const result = new Map<string, string[]>();
  for (const entry of entries) {
    const violations = scanTechniqueEntry(entry);
    if (violations.length > 0) result.set(entry.key, violations);
  }
  return result;
}
